export interface BrowserDocumentUploadFile {
  arrayBuffer(): Promise<ArrayBuffer>;
  name: string;
  size: number;
  type: string;
}

export interface PreparedDocumentUploadPayload {
  byteLength: number;
  bytes: ArrayBuffer;
  filename: string;
  mediaType: string;
  sha256Hex: string;
  title: string;
}

const documentUploadAcceptedMediaTypes = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "text/plain",
].join(",");

const documentUploadAcceptedExtensions = [
  ".doc",
  ".docx",
  ".jpeg",
  ".jpg",
  ".pdf",
  ".png",
  ".txt",
].join(",");

export const browserDocumentUploadAccept = [
  documentUploadAcceptedMediaTypes,
  documentUploadAcceptedExtensions,
].join(",");

export function supportsBrowserDocumentUpload(): boolean {
  return (
    typeof document !== "undefined" &&
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.subtle !== "undefined"
  );
}

function resolveDocumentUploadFilename(file: BrowserDocumentUploadFile): string {
  const trimmedName = file.name.trim();

  return trimmedName.length > 0 ? trimmedName : "document-upload";
}

function resolveDocumentUploadTitle(filename: string): string {
  const withoutExtension = filename.replace(/\.[^.]+$/u, "").trim();

  return withoutExtension.length > 0 ? withoutExtension : filename;
}

function resolveDocumentUploadMediaType(
  file: BrowserDocumentUploadFile,
): string {
  const mediaType = file.type.trim().toLowerCase();

  return mediaType.length > 0 ? mediaType : "application/octet-stream";
}

function encodeHex(bytes: Uint8Array): string {
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join(
    "",
  );
}

export async function prepareBrowserDocumentUploadPayload(
  file: BrowserDocumentUploadFile,
): Promise<PreparedDocumentUploadPayload> {
  const bytes = await file.arrayBuffer();
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  const filename = resolveDocumentUploadFilename(file);

  return {
    byteLength: bytes.byteLength,
    bytes,
    filename,
    mediaType: resolveDocumentUploadMediaType(file),
    sha256Hex: encodeHex(new Uint8Array(digest)),
    title: resolveDocumentUploadTitle(filename),
  };
}

export function pickBrowserDocumentUploadFile(): Promise<
  BrowserDocumentUploadFile | null
> {
  if (typeof document === "undefined") {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const input = document.createElement("input");

    input.accept = browserDocumentUploadAccept;
    input.style.display = "none";
    input.type = "file";

    const cleanup = () => {
      input.remove();
    };

    input.addEventListener(
      "change",
      () => {
        const selectedFile = input.files?.[0] ?? null;
        cleanup();
        resolve(selectedFile);
      },
      { once: true },
    );

    document.body.appendChild(input);
    input.click();
  });
}

export async function uploadPreparedDocumentBytes(
  input: {
    bytes: ArrayBuffer;
    uploadHeaders: Record<string, string>;
    uploadUrl: string;
  },
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(input.uploadUrl, {
    body: input.bytes,
    credentials: "omit",
    headers: input.uploadHeaders,
    method: "PUT",
    signal,
  });

  if (!response.ok) {
    throw new Error(`document-upload-object-put-failed-${response.status}`);
  }
}
